import { reactive } from "vue";
import { authUrl, execute } from "@/api";

/**
 * Holiday dates keyed by YYYY-MM-DD, shared by every leave calendar on the page
 * so the picker and the day counters always agree.
 */
export const holidayMap = reactive({});

const loadedYears = new Set();
const pendingYears = new Map();

export function toDateKey(value) {
  if (!value) return "";
  if (typeof value === "string") {
    return value.slice(0, 10);
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

export function parseDateKey(key) {
  const parts = String(key || "").slice(0, 10).split("-");
  if (parts.length !== 3) return null;
  const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function isWeekendKey(key) {
  const d = parseDateKey(key);
  if (!d) return false;
  const day = d.getDay();
  return day === 0 || day === 6;
}

export function isHolidayKey(key) {
  return Boolean(holidayMap[key]);
}

export function holidayName(key) {
  return holidayMap[key] || "";
}

export function isNonWorkingKey(key) {
  return isWeekendKey(key) || isHolidayKey(key);
}

export async function loadHolidayYear(year) {
  const y = Number(year);
  if (!y || loadedYears.has(y)) return;
  if (pendingYears.has(y)) {
    await pendingYears.get(y);
    return;
  }

  const request = (async () => {
    const data = new FormData();
    data.append("function", "getHolidayDates");
    data.append("year", String(y));
    try {
      const res = await execute(data, authUrl);
      const holidays = res?.data?.data?.holidays || [];
      holidays.forEach((h) => {
        const key = toDateKey(h.date);
        if (key) holidayMap[key] = h.name || "Public holiday";
      });
    } catch (error) {
      // Holidays are advisory: keep the pickers usable with weekends only.
      console.warn("[StaffSync] Could not load holidays for", y, error?.message || error);
    } finally {
      loadedYears.add(y);
      pendingYears.delete(y);
    }
  })();

  pendingYears.set(y, request);
  await request;
}

export async function loadHolidaysForRange(startKey, endKey) {
  const start = parseDateKey(startKey);
  const end = parseDateKey(endKey);
  if (!start || !end) return;
  const years = [];
  for (let y = start.getFullYear(); y <= end.getFullYear(); y++) {
    years.push(y);
  }
  await Promise.allSettled(years.map((y) => loadHolidayYear(y)));
}

/**
 * Working days between two dates, excluding weekends and holidays.
 */
export function countWorkingDays(startKey, endKey) {
  const start = parseDateKey(startKey);
  const end = parseDateKey(endKey);
  if (!start || !end || end < start) return 0;

  let days = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    const key = toDateKey(cursor);
    if (!isNonWorkingKey(key)) days++;
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

/**
 * Non-working dates inside a range, for warning the user about their selection.
 */
export function listNonWorkingDays(startKey, endKey) {
  const start = parseDateKey(startKey);
  const end = parseDateKey(endKey);
  if (!start || !end || end < start) return [];

  const result = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    const key = toDateKey(cursor);
    if (isNonWorkingKey(key)) {
      result.push({
        date: key,
        reason: isHolidayKey(key) ? holidayName(key) : "Weekend",
      });
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return result;
}
